'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { bg0, bgPrimaryText } from '@osmosis/tailwind-ui/styles/background';
import { borderNeutral200 } from '@osmosis/tailwind-ui/styles/border';
import { text0, text5 } from '@osmosis/tailwind-ui/styles/text';
import type { MobileSection, SectionId } from './sections';

type Props = {
  sections: MobileSection[];
  activeSection: SectionId | null;
  onSelect: (id: SectionId) => void;
  className?: string;
};

/**
 * Fixed bottom navigation for the mobile sectioned-panel layout. Renders one
 * button per available section; tapping toggles the section overlay (selecting
 * the active section again closes it). Visibility (mobile-only) is controlled
 * by the parent via `className`.
 */
const MobileSectionNav = ({
  sections,
  activeSection,
  onSelect,
  className,
}: Props): JSX.Element | null => {
  const { formatMessage } = useIntl();

  if (!sections.length) return null;

  return (
    <Flex
      role="tablist"
      aria-label={formatMessage({
        id: 'panel.mobile_nav.label',
        defaultMessage: 'Page sections',
      })}
      data-testid="mobile-section-nav"
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 items-stretch justify-around border-t px-2 py-2',
        bg0,
        borderNeutral200,
        className,
      )}
    >
      {sections.map((section) => {
        const isActive = section.id === activeSection;
        return (
          <Button
            key={section.id}
            role="tab"
            aria-selected={isActive}
            variant="transparent"
            color="transparent"
            onClick={() => onSelect(section.id)}
            data-testid={`mobile-section-nav-${section.id}`}
            className={cn(
              'flex-col items-center justify-center gap-0.5 rounded-full px-8 py-2 text-base transition-colors',
              isActive
                ? cn(bgPrimaryText, text0, 'hover:bg-blue-600')
                : cn(text5, 'hover:bg-blue-100 hover:text-darkest-blue-500'),
            )}
          >
            <Icon icon={section.icon} size={16} />
            {section.label}
          </Button>
        );
      })}
    </Flex>
  );
};

export default MobileSectionNav;
